import Swal from "sweetalert2";
import { IEmpresas } from "../interfaces/IEmpresas";
import { IEsteira } from "../interfaces/IEsteira";
import { IConciliados, IConta, ILancamento } from "../interfaces/IRelatorio";
import api from './Api';

class EmpresasService {
    async obterTodos(): Promise<IEmpresas[]> {
        const { data: result } = await api.get<IEmpresas[]>('Empresa');

        return result;
    }

    async obterTodosPorClienteID(clienteID: number): Promise<IEmpresas[]> {
        const { data: result } = await api.get<IEmpresas[]>('Empresa/BuscarPorClienteID/' + clienteID);

        return result;
    }

    async obterPorId(empresaID: number): Promise<IEmpresas> {
        const { data: result } = await api.get<IEmpresas>(`Empresa/${empresaID}`);

        return result;
    }

    async criar(empresa: IEmpresas): Promise<IEmpresas> {
        const { data: result } = await api.post('Empresa', { ...empresa, importacaoEspecial: false, agruparContas: false });

        return result;
    }

    async atualizar(empresa: IEmpresas): Promise<IEmpresas> {
        const { data: result } = await api.put(`Empresa/${empresa.id}`, { ...empresa, importacaoEspecial: false, agruparContas: false, esteiraImportando: 0 });

        return result;
    }

    async excluir(empresaID: number): Promise<void> {
        const { data: result } = await api.delete(`Empresa/${empresaID}`);

        return result;
    }

    async obterLogo(empresaID: number): Promise<string | null> {
        const { data: result } = await api.get<{ url: string | null }>(`Empresa/${empresaID}/logo`, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });

        return result.url;
    }

    async enviarLogo(empresaID: number, arquivo: File): Promise<string> {
        const formData = new FormData();
        formData.append('file', arquivo);

        const { data: result } = await api.post<{ url: string }>(`Empresa/${empresaID}/logo`, formData, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/',
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        return result.url;
    }

    async removerLogo(empresaID: number): Promise<void> {
        await api.delete(`Empresa/${empresaID}/logo`, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
    }

    async obterContabilizacaoFolha(empresaID: number): Promise<boolean> {
        const { data: result } = await api.get<{ contabilizaFolhaLiquido: boolean }>(`Empresa/${empresaID}/contabilizacao-folha`, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });

        return result.contabilizaFolhaLiquido;
    }

    async salvarContabilizacaoFolha(empresaID: number, contabilizaFolhaLiquido: boolean): Promise<void> {
        await api.put(`Empresa/${empresaID}/contabilizacao-folha`, { contabilizaFolhaLiquido }, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
    }

    async obterConciliaPosicaoFinanceira(empresaID: number): Promise<boolean> {
        const { data: result } = await api.get<{ conciliaPosicaoFinanceira: boolean }>(`Empresa/${empresaID}/posicao-financeira`, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });

        return result.conciliaPosicaoFinanceira;
    }

    async salvarConciliaPosicaoFinanceira(empresaID: number, conciliaPosicaoFinanceira: boolean): Promise<void> {
        await api.put(`Empresa/${empresaID}/posicao-financeira`, { conciliaPosicaoFinanceira }, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
    }

    async obterTodosPorUsuarioId(usuarioID: number): Promise<IEmpresas[]> {
        const { data: result } = await api.get<IEmpresas[]>('Empresa/BuscarPorUsuarioID/' + usuarioID);

        return result;
    }

    async obterEsteiras(empresaID: number): Promise<IEsteira[]> {
        const { data: result } = await api.get<IEsteira[]>('Esteira/empresa/' + empresaID, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3'
        });
        return result;
    }

    async obterContasEsteira(esteiraID: number): Promise<IConta[]> {
        const { data: result } = await api.get<IConta[]>('Esteira/BuscarContas/' + esteiraID);
        return result;
    }

    async obterConta(esteiraID: number, contaID: String): Promise<IConta> {
        const { data: result } = await api.get<IConta>('Esteira/BuscarConta/' + esteiraID + '/' + contaID);
        return result;
    }

    async obeterLancamentosPendentesEsteira(esteiraID: number, codigoContabil: string, filter: String): Promise<ILancamento[]> {
        let endpoint = 'Conta/ListarPendentes/' + esteiraID + '/' + codigoContabil;
        if (filter) {
            endpoint += '?q=' + filter;
        }
        const { data: result } = await api.get<ILancamento[]>(endpoint);
        return result;
    }

    async contarContas(esteiraID: number): Promise<number> {
        const { data: result } = await api.get<number>('Conta/TotalContas/' + esteiraID);
        return result;
    }

    async contarTotalContasPendentes(esteiraID: number): Promise<number> {
        const { data: result } = await api.get<number>('Conta/TotalContasPendentes/' + esteiraID);
        return result;
    }

    async conciliarLancamentos(ids: number[], contaManter: any = null): Promise<IEmpresas> {
        const { data: result } = await api.post('Conciliation/Conciliar', ids);
        return result;
    }

    async vincularEmMassa(payload: {
        esteiraId: number;
        cartaoId: number;
        creditos: { ids: number[]; selecionarTodos: boolean; filtro?: { ano?: number; mes?: number; dia?: number; historico?: string; valor?: number } };
        debitos: { ids: number[]; selecionarTodos: boolean; filtro?: { ano?: number; mes?: number; dia?: number; historico?: string; valor?: number } };
    }): Promise<{ operacaoId: number }> {
        const { data: result } = await api.post('Conciliation/vincular-em-massa', payload);
        return result;
    }

    async conciliarTodos(esteiraID: number, contaContabil: String): Promise<any> {
        await api.put(`Conta/ConciliarTodos/${esteiraID}/${contaContabil}`, null, {
            baseURL: process.env.REACT_APP_API_URL ?? 'https://desenvolvimento.painel-agholmes-1.com.br/api/',
        });
    }


    async conciliarMuitosLancamentos(ids: number[]): Promise<IEmpresas> {
        const { data: result } = await api.post('Conta/ConciliarMuitos', ids);
        return result;
    }

    async vincularMatch(esteiraID: number, cardId: number, keys: string[]): Promise<IEmpresas> {
        const { data: result } = await api.post(`Aprovacao/${esteiraID}/conciliacao/${cardId}/aprovar-match`, keys, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
        return result;
    }

    async desvincularMatch(esteiraID: number, cardId: number, keys: string[]): Promise<IEmpresas> {
        const { data: result } = await api.post(`Aprovacao/${esteiraID}/conciliacao/${cardId}/reprovar-match`, keys, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
        return result;
    }

    async buscaRapida(esteiraID: number, conta: String, historico: string, valor?: number): Promise<IConciliados> {
        const { data: result } = await api.post<IConciliados>('Conta/BuscaRapida/' + esteiraID + '/' + conta, {
            valor,
            historico
        });
        return result;
    }

    async buscaInteligente(esteiraID: number, conta: String): Promise<IConciliados> {
        try {
            const { data: result } = await api.get<IConciliados>('Conta/BuscaInteligente/' + esteiraID + '/' + conta);
            return result;
        } catch (e) {
            Swal.fire({
                text: 'Não foi encontrado pares próximos para realizar a busca inteligente',
                timer: 3000,
                icon: 'error',
                showConfirmButton: false
            })

            return { creditos: [], debitos: [] };
        }
    }

    async processarRelatorioEstorno(esteiraID: number, mes: number, ano: number): Promise<void> {
        await api.get(`Relatorio/GerarRelatorioEstornoExcel/${esteiraID}/${mes}/${ano}`, {
            baseURL: process.env.REACT_APP_API_URL ?? 'https://desenvolvimento.painel-agholmes-1.com.br/api/'
        });
        Swal.fire({
            text: "Após processado o arquivo será enviado em seu e-mail!",
            timer: 3000,
            icon: 'success',
            showConfirmButton: false
        });
    }

    enviarArquivoCadastroClienteFornecedor(empresaID: number, url: string, linha: string, nome: string): Promise<void> {
        return api.post(`ClienteFornecedorEmpresa/${empresaID}`, {
            baseURL: process.env.REACT_APP_API_URL ?? 'https://desenvolvimento.painel-agholmes-1.com.br/api/',
            body: {
                url,
                nome,
                linha
            }
        });
    }

    async buscarRelatorioUtilizacaoUsuario(empresaID: number) {
        const { data: result } = await api.get<any[]>(`RelatorioUtilizacao/${empresaID}`);
        return result;
    }

    async transportarSaldo(esteiraID: number): Promise<void> {
        await api.post(`Esteira/${esteiraID}/transportar-saldo`, null, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
    }

    async buscarOperacoesEsteira(esteiraID: number): Promise<any[]> {
        const { data: result } = await api.get<any[]>(`Esteira/${esteiraID}/operacoes`, {
            baseURL: process.env.REACT_APP_API_CORE_URL ?? 'https://api-core.dev.painel-agholmes.com/api/v3/'
        });
        return result;
    }
}


export default new EmpresasService();